import { useState } from 'react';
import { AxiosError } from 'axios';
import { Box, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
    StyledDialog,
    StyledDialogContainer
} from '@/components/consent/StyledDialog';
import {
    OutlinedSecondaryButtonLG,
    PrimaryButtonLG
} from '@/components/shared/Button';
import { InputText } from '@/components/shared/InputText';
import { Dropdown } from '@/components/shared/Dropdown';
import { SMBold, SMSemiBold } from '@/typography/Typography';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { useErrorHandling } from '@/utils/errorHandling';
import {
    isValidBic,
    isValidCvr,
    isValidIban,
    normaliseBic,
    normaliseCvr,
    normaliseIban
} from '@/utils/bankMaterialValidation';
import { BankDetailsFields } from './BankDetailsFields';

// The backend only accepts these two currencies (validated server-side); the
// dropdown mirrors that fixed set.
const SUPPORTED_CURRENCIES = ['DKK', 'EUR'] as const;

interface CreateHouseAccountDialogProps {
    open: boolean;
    onClose: () => void;
}

export const CreateHouseAccountDialog = ({
    open,
    onClose
}: CreateHouseAccountDialogProps) => {
    const { t } = useTranslation();
    const { setAlert } = useNotificationsSnackbar();
    const { backendErrorHandler } = useErrorHandling();
    const { createHouseAccount } = usePaymentsMutations();

    const [name, setName] = useState('');
    const [currency, setCurrency] = useState('');
    const [accountHolderName, setAccountHolderName] = useState('');
    const [cvr, setCvr] = useState('');
    const [iban, setIban] = useState('');
    const [bic, setBic] = useState('');
    const [nameTaken, setNameTaken] = useState(false);
    // Field-format errors surface only after the first submit attempt, then
    // stay live so the operator sees each field clear as they fix it.
    const [submitAttempted, setSubmitAttempted] = useState(false);

    const resetAndClose = () => {
        // Bank material is write-only: never retain it in component state after
        // the dialog closes so it can never be re-rendered.
        setName('');
        setCurrency('');
        setAccountHolderName('');
        setCvr('');
        setIban('');
        setBic('');
        setNameTaken(false);
        setSubmitAttempted(false);
        onClose();
    };

    const holderNameValid = accountHolderName.trim().length > 0;
    const cvrValid = isValidCvr(cvr.trim());
    const ibanValid = isValidIban(iban);
    const bicValid = isValidBic(bic);

    const allPresent =
        name.trim().length > 0 &&
        !!currency &&
        accountHolderName.trim().length > 0 &&
        cvr.trim().length > 0 &&
        iban.trim().length > 0 &&
        bic.trim().length > 0;

    const canCreate = allPresent && !createHouseAccount.isPending;

    const handleCreate = () => {
        setSubmitAttempted(true);
        if (!(holderNameValid && cvrValid && ibanValid && bicValid)) {
            return;
        }

        createHouseAccount.mutate(
            {
                name: name.trim(),
                currency,
                accountHolderName: accountHolderName.trim(),
                // Ship the same normalised form the client validated, so the
                // payload never carries internal spaces or mixed case even
                // though the backend also normalises.
                cvr: normaliseCvr(cvr),
                iban: normaliseIban(iban),
                bic: normaliseBic(bic)
            },
            {
                onSuccess: () => {
                    setAlert(t('house_accounts.create_success'), 'success');
                    resetAndClose();
                },
                onError: (error) => {
                    const status = (error as AxiosError)?.response?.status;
                    // Duplicate name is a friendly inline error on the field; the
                    // dialog stays open so the operator can rename and retry.
                    if (status === 409) {
                        setNameTaken(true);
                        return;
                    }
                    // Secret-write failure: the row was rolled back server-side,
                    // so the operator can simply retry. Keep the dialog open.
                    if (status === 502) {
                        setAlert(
                            t('house_accounts.secret_write_failed_error'),
                            'error'
                        );
                        return;
                    }
                    backendErrorHandler()(error);
                }
            }
        );
    };

    return (
        <StyledDialog open={open} onClose={resetAndClose}>
            <StyledDialogContainer style={{ width: '440px', padding: '24px' }}>
                <SMSemiBold mb={2}>
                    {t('house_accounts.add_account')}
                </SMSemiBold>

                <InputText
                    testid="house-account-name"
                    label={t('house_accounts.name')}
                    required
                    value={name}
                    onChange={(event) => {
                        setName(event.target.value);
                        if (nameTaken) {
                            setNameTaken(false);
                        }
                    }}
                    error={nameTaken}
                    helperText={
                        nameTaken
                            ? t('house_accounts.name_taken_error')
                            : undefined
                    }
                />

                <Box mt={2}>
                    <SMSemiBold mb={0.5}>
                        {t('house_accounts.currency')}
                    </SMSemiBold>
                    <Dropdown
                        label={t('house_accounts.currency')}
                        value={currency}
                        choices={SUPPORTED_CURRENCIES.map((code) => ({
                            label: code,
                            value: code
                        }))}
                        onChange={(value) => setCurrency(value as string)}
                    />
                </Box>

                <Box mt={2}>
                    <BankDetailsFields
                        required
                        accountHolderName={accountHolderName}
                        onAccountHolderNameChange={setAccountHolderName}
                        cvr={cvr}
                        onCvrChange={setCvr}
                        iban={iban}
                        onIbanChange={setIban}
                        bic={bic}
                        onBicChange={setBic}
                        holderNameError={submitAttempted && !holderNameValid}
                        cvrError={submitAttempted && !cvrValid}
                        ibanError={submitAttempted && !ibanValid}
                        bicError={submitAttempted && !bicValid}
                    />
                </Box>

                <Stack
                    direction="row"
                    justifyContent="flex-end"
                    gap={1.5}
                    mt={3}
                >
                    <OutlinedSecondaryButtonLG
                        color="secondary"
                        style={{ height: '40px', borderRadius: '8px' }}
                        onClick={resetAndClose}
                    >
                        <SMBold textTransform="none">{t('Cancel')}</SMBold>
                    </OutlinedSecondaryButtonLG>
                    <PrimaryButtonLG
                        style={{ height: '40px', borderRadius: '8px' }}
                        disabled={!canCreate}
                        onClick={handleCreate}
                    >
                        <SMBold textTransform="none">
                            {t('house_accounts.add_account')}
                        </SMBold>
                    </PrimaryButtonLG>
                </Stack>
            </StyledDialogContainer>
        </StyledDialog>
    );
};

export default CreateHouseAccountDialog;
